import React, { useEffect, useState, useRef, useCallback } from 'react';
import katex from 'katex';                 // ✅ V8 수식 렌더링
import 'katex/dist/katex.min.css';         // ✅ V8 수식 글꼴/스타일
import { questionAPI, adminAPI } from '../../api';
import { useAuthStore } from '../../store';
import RightPanel from './RightPanel';
import './Exam.css';

interface Props {
  subject: any;
  examType: '필기' | '실기';
  onBack: () => void;
  onModeChange?: (mode: 'normal' | 'mock' | 'wrong') => void;
}

// 과목별 색상 팔레트 (동적 할당)
const COLOR_PALETTE = [
  '#e74c3c', '#e67e22', '#f39c12', '#2ecc71',
  '#3498db', '#9b59b6', '#1abc9c', '#e91e63',
  '#00bcd4', '#ff5722',
];
const subjectColorMap: Record<string, string> = {};
let colorIndex = 0;
const getSubjectColor = (name: string) => {
  if (!name) return '#7f8c8d';
  if (!subjectColorMap[name]) {
    subjectColorMap[name] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
    colorIndex++;
  }
  return subjectColorMap[name];
};


// 드래그 리사이저 훅
const useResizer = (initialWidth: number, min: number, max: number, direction: 'right' | 'left' = 'right') => {
  const [width, setWidth] = useState(initialWidth);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(initialWidth);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const adjusted = direction === 'right' ? delta : -delta;
      const newWidth = Math.min(max, Math.max(min, startWidth.current + adjusted));
      setWidth(newWidth);
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [min, max, direction]);

  return { width, onMouseDown };
};

const MockExam: React.FC<Props> = ({ subject, examType, onBack, onModeChange }) => {
  const { user } = useAuthStore();
  const sidebarR = useResizer(200, 140, 340, 'right');
  const answerSheetR = useResizer(260, 160, 360, 'left');
  const restPanelR = useResizer(180, 120, 320, 'left');
  const rightPanelR = useResizer(500, 200, 900, 'left'); // ✅ RightPanel
  const [allQuestions, setAllQuestions] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [mockType, setMockType] = useState<'일반' | 'D' | 'R' | 'S'>('일반');
  const [curIdx, setCurIdx] = useState(0);
  const [userAns, setUserAns] = useState<Record<number, any>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [showAnswerSheet, setShowAnswerSheet] = useState(true);
  const [showRemaining, setShowRemaining] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoCanClose, setVideoCanClose] = useState(false);
  const [videoCountdown, setVideoCountdown] = useState(5);

  const openVideo = (url: string) => {
    if (!url?.trim()) return;
    setVideoUrl(url.trim());
    setVideoCanClose(false);
    setVideoCountdown(5);
    let count = 5;
    const timer = setInterval(() => {
      count--;
      setVideoCountdown(count);
      if (count <= 0) { clearInterval(timer); setVideoCanClose(true); }
    }, 1000);
  };
  const getYouTubeId = (url: string) => url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1] || null;
  const [showExp, setShowExp] = useState<Record<number, boolean>>({});
  const [showComment, setShowComment] = useState(false);

  // ✅ 문제검토 필터 (년도/차수)
  const [reviewMode, setReviewMode] = useState(false); // 문제검토 모드
  const [reviewYear, setReviewYear] = useState('');
  const [reviewRound, setReviewRound] = useState('');
  // 모의고사에 출제된 year/round 목록 (중복 제거)
  const reviewYears = Array.from(new Set(questions.map(q => String(q.year)).filter(Boolean))).sort();
  const reviewRounds = Array.from(new Set(
    questions.filter(q => !reviewYear || String(q.year) === reviewYear).map(q => String(q.round)).filter(Boolean)
  )).sort((a, b) => Number(a) - Number(b));
  // 필터링된 문제검토 목록
  const reviewQuestions = questions.filter(q =>
    (!reviewYear || String(q.year) === reviewYear) &&
    (!reviewRound || String(q.round) === reviewRound)
  );
  // 실기 전용 state
  const [silgiStatus, setSilgiStatus] = useState<Record<number, 'solved'|'unsolved'|'correct'|'wrong'>>({});
  const [silgiScores, setSilgiScores] = useState<Record<number, number>>({}); // 문제별 점수

  // 카운트다운 타이머 (필기 150분=9000초, 실기 100분=6000초)
  const [remaining, setRemaining] = useState(examType === '필기' ? 9000 : 6000);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 휴식박스
  const [displayBoxes, setDisplayBoxes] = useState<string[]>([]);
  const [restBoxes, setRestBoxes] = useState<string[]>([]);
  const [restInterval, setRestInterval] = useState(300);
  const [restCount, setRestCount] = useState(2);
  const [fontSize, setFontSize] = useState(17);
  const restScrollRef = useRef<HTMLDivElement>(null);
  const mockListRef = useRef<HTMLDivElement>(null);
  const mockQuestionsLengthRef = useRef(0);
  const mockIsFirstRender = useRef(true);

  // ✅ ← → 키 문제 이동
  useEffect(() => {
    mockQuestionsLengthRef.current = questions.length;
  }, [questions.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        if (tag === 'BUTTON') (e.target as HTMLElement).blur();
        if (e.key === 'ArrowRight') {
          setCurIdx(i => Math.min(mockQuestionsLengthRef.current - 1, i + 1));
        } else {
          setCurIdx(i => Math.max(0, i - 1));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  // ✅ 사이드바 중앙 고정
  useEffect(() => {
    if (mockIsFirstRender.current) { mockIsFirstRender.current = false; return; }
    if (!mockListRef.current) return;
    const container = mockListRef.current;
    const items = container.querySelectorAll('.mock-q-item');
    const activeEl = items[curIdx] as HTMLElement;
    if (!activeEl) return;
    const containerH = container.clientHeight;
    const itemTop = activeEl.offsetTop - container.offsetTop;
    const itemH = activeEl.clientHeight;
    container.scrollTop = Math.max(0, itemTop - containerH / 2 + itemH / 2);
  }, [curIdx]);

  // 년도/문제수 필터
  const [filterYear, setFilterYear] = useState('');
  const [mockCount, setMockCount] = useState(examType === '필기' ? 100 : 20);

  useEffect(() => {
    questionAPI.list({ subject_id: subject.id, exam_type: examType }).then(res => {
      setAllQuestions(res.data);
      setLoading(false);
    });
    adminAPI.restBoxes().then(res => {
      const boxes = res.data.map((b: any) => b.content).filter(Boolean);
      setRestBoxes(boxes);
      setDisplayBoxes([...boxes]);
    }).catch(() => {});
    adminAPI.settings().then(res => {
      setRestInterval(res.data.rest_normal_interval ?? 300);
      setRestCount(res.data.rest_rotation_count ?? 2);
      // 폰트 크기 적용 (필기/실기 모의고사)
      const key = examType === '필기' ? 'font_size_pilgi_mock' : 'font_size_silgi_mock';
      setFontSize(res.data[key] ?? 17);
    }).catch(() => {});
  }, [subject, examType]);

  // 휴식박스 자동 로테이션
  useEffect(() => {
    if (restBoxes.length < 2) return;
    const timer = setInterval(() => {
      setDisplayBoxes(prev => {
        const count = Math.min(restCount, prev.length - 1);
        return [...prev.slice(count), ...prev.slice(0, count)];
      });
    }, restInterval * 1000);
    return () => clearInterval(timer);
  }, [restBoxes, restCount, restInterval]);

  useEffect(() => {
    if (!restScrollRef.current) return;
    restScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [displayBoxes]);

  // 타이머
  useEffect(() => {
    if (questions.length === 0 || submitted) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) {
          clearInterval(timerRef.current!);
          handleAutoSubmit();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [questions, submitted]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAutoSubmit = () => {
    alert('시간이 종료되었습니다! 자동으로 제출됩니다.');
    setSubmitted(true);
    setShowResult(true);
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const years = Array.from(new Set(allQuestions.map(q => String(q.year)))).filter(Boolean).sort();

  // 일반 모의고사
  const startNormal = useCallback(() => {
    let pool = allQuestions;
    if (filterYear) pool = pool.filter(q => String(q.year) === filterYear);
    if (pool.length === 0) { alert('해당 조건의 문제가 없습니다.'); return; }

    let selected: any[] = [];

    if (examType === '필기') {
      // 필기: 과목별 20문제씩, 과목 순서 유지
      const subjectOrder: string[] = [];
      pool.forEach(q => {
        const s = q.subject_name || '';
        if (s && !subjectOrder.includes(s)) subjectOrder.push(s);
      });
      subjectOrder.forEach(subj => {
        const subQ = pool.filter(q => (q.subject_name || '') === subj);
        const shuffled = [...subQ].sort(() => Math.random() - 0.5);
        selected.push(...shuffled.slice(0, 20));
      });
      // 부족하면 전체에서 채우기
      const totalCount = 100;
      if (selected.length < totalCount) {
        const existIds = new Set(selected.map(q => q.id));
        const remain = pool.filter(q => !existIds.has(q.id));
        selected.push(...[...remain].sort(() => Math.random() - 0.5).slice(0, totalCount - selected.length));
      }
      // 과목 순서로 정렬
      selected.sort((a, b) => subjectOrder.indexOf(a.subject_name || '') - subjectOrder.indexOf(b.subject_name || ''));
    } else {
      // 실기: 난이도 상20% + 중50% + 하30%, 20문항
      const totalCount = 20;
      const highQ = pool.filter(q => q.difficulty === '상');
      const midQ = pool.filter(q => q.difficulty === '중');
      const lowQ = pool.filter(q => q.difficulty === '하');
      const highCount = Math.round(totalCount * 0.2);
      const midCount = Math.round(totalCount * 0.5);
      const lowCount = totalCount - highCount - midCount;
      selected = [
        ...[...highQ].sort(() => Math.random() - 0.5).slice(0, Math.min(highCount, highQ.length)),
        ...[...midQ].sort(() => Math.random() - 0.5).slice(0, Math.min(midCount, midQ.length)),
        ...[...lowQ].sort(() => Math.random() - 0.5).slice(0, Math.min(lowCount, lowQ.length)),
      ].sort(() => Math.random() - 0.5);
      // 부족하면 채우기
      if (selected.length < totalCount) {
        const existIds = new Set(selected.map(q => q.id));
        const remain = pool.filter(q => !existIds.has(q.id));
        selected.push(...[...remain].sort(() => Math.random() - 0.5).slice(0, totalCount - selected.length));
      }
    }

    if (selected.length === 0) { alert('문제가 없습니다.'); return; }
    setQuestions(selected);
    setMockType('일반');
    setUserAns({}); setSubmitted(false); setShowResult(false); setCurIdx(0);
    setRemaining(examType === '필기' ? 9000 : 6000);
  }, [allQuestions, filterYear, examType]);

  // D모의고사: 필기=과목별 상70%+중30% 20문항씩 / 실기=전체 상70%+중30% 20문항
  const startD = useCallback(() => {
    if (allQuestions.length === 0) { alert('문제가 없습니다.'); return; }
    const totalCount = 20;
    let selected: any[] = [];

    if (examType === '필기') {
      // 과목 순서 유지
      const subjectOrder: string[] = [];
      allQuestions.forEach(q => {
        const s = q.subject_name || '';
        if (s && !subjectOrder.includes(s)) subjectOrder.push(s);
      });

      subjectOrder.forEach(subj => {
        const subjectQ = allQuestions.filter(q => (q.subject_name || '') === subj);
        const highQ = subjectQ.filter(q => q.difficulty === '상');
        const midQ  = subjectQ.filter(q => q.difficulty === '중');

        const highCount = Math.round(totalCount * 0.7); // 14문항
        const midCount  = totalCount - highCount;        // 6문항

        const selHigh = [...highQ].sort(() => Math.random() - 0.5).slice(0, Math.min(highCount, highQ.length));
        const selMid  = [...midQ].sort(() => Math.random() - 0.5).slice(0, Math.min(midCount, midQ.length));
        let subSel = [...selHigh, ...selMid].sort(() => Math.random() - 0.5);

        // 부족하면 해당 과목 전체에서 채우기
        if (subSel.length < totalCount) {
          const existIds = new Set(subSel.map(q => q.id));
          const remain = subjectQ.filter(q => !existIds.has(q.id));
          subSel.push(...[...remain].sort(() => Math.random() - 0.5).slice(0, totalCount - subSel.length));
        }
        selected.push(...subSel);
      });

      // 과목 순서대로 정렬
      selected.sort((a, b) => {
        const oa = subjectOrder.indexOf(a.subject_name || '');
        const ob = subjectOrder.indexOf(b.subject_name || '');
        return oa - ob;
      });

    } else {
      // 실기: 전체에서 상70% + 중30%, 20문항
      const highQ = allQuestions.filter(q => q.difficulty === '상');
      const midQ  = allQuestions.filter(q => q.difficulty === '중');

      const highCount = Math.round(totalCount * 0.7);
      const midCount  = totalCount - highCount;

      selected = [
        ...[...highQ].sort(() => Math.random() - 0.5).slice(0, Math.min(highCount, highQ.length)),
        ...[...midQ].sort(() => Math.random() - 0.5).slice(0, Math.min(midCount, midQ.length)),
      ].sort(() => Math.random() - 0.5);

      // 부족하면 전체에서 채우기
      if (selected.length < totalCount) {
        const existIds = new Set(selected.map(q => q.id));
        const remain = allQuestions.filter(q => !existIds.has(q.id));
        selected.push(...[...remain].sort(() => Math.random() - 0.5).slice(0, totalCount - selected.length));
        selected = selected.sort(() => Math.random() - 0.5);
      }
    }

    if (selected.length === 0) { alert('문제가 없습니다.'); return; }

    setQuestions(selected);
    setMockType('D');
    setUserAns({}); setSubmitted(false); setShowResult(false); setCurIdx(0);
    setRemaining(examType === '필기' ? 9000 : 6000);
  }, [allQuestions, examType]);

  // R모의고사: 최근 7개년, 필기=과목순서유지 100문제 / 실기=난이도비율 20문제
  const startR = useCallback(() => {
    if (allQuestions.length === 0) { alert('문제가 없습니다.'); return; }

    // 최근 7개년 필터링
    const yearNums = allQuestions.map(q => parseInt(q.year) || 0).filter(y => y > 0);
    const maxYear = yearNums.length > 0 ? Math.max(...yearNums) : 0;
    const minYear = maxYear - 6;
    const recentQ = maxYear > 0
      ? allQuestions.filter(q => { const y = parseInt(q.year) || 0; return y >= minYear && y <= maxYear; })
      : allQuestions;

    const totalCount = examType === '필기' ? 100 : 20;
    const timeLimit  = examType === '필기' ? 9000 : 6000;

    let selected: any[] = [];

    if (examType === '필기') {
      // 과목 순서 유지 (allQuestions 원본 순서 기준)
      const subjectOrder: string[] = [];
      allQuestions.forEach(q => {
        const s = q.subject_name || '';
        if (s && !subjectOrder.includes(s)) subjectOrder.push(s);
      });

      subjectOrder.forEach(subj => {
        // 해당 과목의 최근 7개년 문제
        const subjectQ = recentQ.filter(q => (q.subject_name || '') === subj);
        selected.push(...[...subjectQ].sort(() => Math.random() - 0.5).slice(0, 20));
      });

      // 부족하면 최근 7개년에서 랜덤 추가
      if (selected.length < totalCount) {
        const existIds = new Set(selected.map(q => q.id));
        const remain = recentQ.filter(q => !existIds.has(q.id));
        selected.push(...[...remain].sort(() => Math.random() - 0.5).slice(0, totalCount - selected.length));
      }

      // 과목 순서대로 정렬
      selected.sort((a, b) => {
        const oa = subjectOrder.indexOf(a.subject_name || '');
        const ob = subjectOrder.indexOf(b.subject_name || '');
        return oa - ob;
      });

    } else {
      // 실기: 상 20% / 중 50% / 하 30%
      const highQ = recentQ.filter(q => q.difficulty === '상');
      const midQ  = recentQ.filter(q => q.difficulty === '중');
      const lowQ  = recentQ.filter(q => q.difficulty === '하');

      const highCount = Math.round(totalCount * 0.2); // 4문항
      const midCount  = Math.round(totalCount * 0.5); // 10문항
      const lowCount  = totalCount - highCount - midCount; // 6문항

      selected = [
        ...[...highQ].sort(() => Math.random() - 0.5).slice(0, Math.min(highCount, highQ.length)),
        ...[...midQ].sort(() => Math.random() - 0.5).slice(0, Math.min(midCount, midQ.length)),
        ...[...lowQ].sort(() => Math.random() - 0.5).slice(0, Math.min(lowCount, lowQ.length)),
      ].sort(() => Math.random() - 0.5);

      // 부족하면 최근 7개년에서 랜덤 추가
      if (selected.length < totalCount) {
        const existIds = new Set(selected.map(q => q.id));
        const remain = recentQ.filter(q => !existIds.has(q.id));
        selected.push(...[...remain].sort(() => Math.random() - 0.5).slice(0, totalCount - selected.length));
        selected = selected.sort(() => Math.random() - 0.5);
      }
    }

    if (selected.length === 0) { alert('최근 7개년 문제가 없습니다.'); return; }

    setQuestions(selected);
    setMockType('R');
    setUserAns({}); setSubmitted(false); setShowResult(false); setCurIdx(0);
    setRemaining(timeLimit);
  }, [allQuestions, examType]);

  // S모의고사: 오답노트 기반 출제
  const startS = useCallback(async () => {
    if (allQuestions.length === 0) { alert('문제가 없습니다.'); return; }

    const totalCount = 20;

    try {
      // 오답노트 로드 (일반 오답, is_mock=false)
      const wrongRes = await questionAPI.wrongAnswers(examType, false);
      const wrongIds = new Set(wrongRes.data.map((w: any) => w.question));

      // 오답노트 문제를 allQuestions에서 찾기 + _isWrong 플래그
      const wrongQuestions = allQuestions
        .filter(q => wrongIds.has(q.id))
        .map(q => ({ ...q, _isWrong: true }));

      let selected: any[] = [];

      if (examType === '필기') {
        // 필기: 과목별로 오답 우선, 20문제씩
        const subjectOrder: string[] = [];
        allQuestions.forEach(q => {
          const s = q.subject_name || '';
          if (s && !subjectOrder.includes(s)) subjectOrder.push(s);
        });

        subjectOrder.forEach(subj => {
          const wrongSubj = wrongQuestions.filter(q => (q.subject_name || '') === subj);
          let subSel = [...wrongSubj].sort(() => Math.random() - 0.5).slice(0, totalCount);

          // 오답 20문제 미만이면 → 해당 과목 상급 문제로 채우기
          if (subSel.length < totalCount) {
            const existIds = new Set(subSel.map(q => q.id));
            const allSubj = allQuestions.filter(q => (q.subject_name || '') === subj && !existIds.has(q.id));
            // 상급 우선, 없으면 랜덤
            const highQ = allSubj.filter(q => q.difficulty === '상');
            const fillPool = highQ.length > 0 ? highQ : allSubj;
            const need = totalCount - subSel.length;
            subSel.push(...[...fillPool].sort(() => Math.random() - 0.5).slice(0, need));
          }
          selected.push(...subSel.slice(0, totalCount));
        });

        // 과목 순서대로 정렬
        selected.sort((a, b) =>
          subjectOrder.indexOf(a.subject_name || '') - subjectOrder.indexOf(b.subject_name || '')
        );

      } else {
        // 실기: 오답 우선 20문제
        selected = [...wrongQuestions].sort(() => Math.random() - 0.5).slice(0, totalCount);

        // 오답 20문제 미만이면 → 상급 문제로 채우기
        if (selected.length < totalCount) {
          const existIds = new Set(selected.map(q => q.id));
          const remaining = allQuestions.filter(q => !existIds.has(q.id));
          const highQ = remaining.filter(q => q.difficulty === '상');
          const fillPool = highQ.length > 0 ? highQ : remaining; // 상급 없으면 랜덤
          const need = totalCount - selected.length;
          selected.push(...[...fillPool].sort(() => Math.random() - 0.5).slice(0, need));
          selected = selected.sort(() => Math.random() - 0.5);
        }
      }

      if (selected.length === 0) { alert('문제가 없습니다.'); return; }

      // 오답 문제 수 안내
      const wrongCount = selected.filter(q => wrongIds.has(q.id)).length;
      const fillCount = selected.length - wrongCount;
      alert(`S모의고사 시작!\n오답노트: ${wrongCount}문제 + 상급보충: ${fillCount}문제`);

      setQuestions(selected);
      setMockType('S');
      setUserAns({}); setSubmitted(false); setShowResult(false); setCurIdx(0);
      setSilgiStatus({}); setSilgiScores({});
      setRemaining(examType === '필기' ? 9000 : 6000);

    } catch (e) {
      alert('오답노트 로드 실패. 다시 시도해주세요.');
    }
  }, [allQuestions, examType]);

  // 자동 시작
  useEffect(() => {
    if (allQuestions.length > 0) startNormal();
  }, [allQuestions]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = questions[curIdx];
  const totalAnswered = Object.keys(userAns).length;

  // 난이도 → 기본 점수 (엑셀 난이도 컬럼: 하=1~4, 중=5~6, 상=7+)
  const diffToScore = (diff: string): number => {
    if (diff === '상') return 7;
    if (diff === '중') return 5;
    return 2; // 하
  };

  // 실기 총점 (정답 처리된 문제들의 점수 합산)
  const silgiTotalScore = submitted
    ? questions.reduce((sum, mq) => {
        if (silgiStatus[mq.id] === 'correct') {
          return sum + (silgiScores[mq.id] ?? diffToScore(mq.difficulty || '하'));
        }
        return sum;
      }, 0)
    : 0;

  // 필기 점수
  const correctCount = submitted
    ? questions.filter(q => String(userAns[q.id]) === String(q.answer)).length : 0;
  const score = submitted ? Math.round(correctCount / questions.length * 100) : 0;

  const handleSubmit = () => {
    if (examType === '필기') {
      if (totalAnswered < questions.length) {
        if (!window.confirm(`${questions.length - totalAnswered}문제를 풀지 않았습니다. 제출하시겠습니까?`)) return;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      setSubmitted(true);
      setShowResult(true);

      // ✅ 필기 결과 저장
      const correctCnt = questions.filter(q => String(userAns[q.id]) === String(q.answer)).length;
      const scoreVal = Math.round(correctCnt / questions.length * 100);

      // 과목별 통계
      const subjectStatsMap: Record<string, { correct: number; total: number }> = {};
      questions.forEach(q => {
        const subj = q.subject_name || '기타';
        if (!subjectStatsMap[subj]) subjectStatsMap[subj] = { correct: 0, total: 0 };
        subjectStatsMap[subj].total++;
        if (String(userAns[q.id]) === String(q.answer)) subjectStatsMap[subj].correct++;
      });

      questionAPI.saveMockResult({
        subject_id: subject.id,
        exam_type: '필기',
        mock_type: mockType,
        score: scoreVal,
        correct_count: correctCnt,
        total_count: questions.length,
        subject_stats: subjectStatsMap,
      }).catch(() => {});

      // ✅ 필기 모의고사 - 틀린 문제 자동 오답노트 저장
      const wrongQs = questions.filter(q => String(userAns[q.id]) !== String(q.answer));
      wrongQs.forEach(q => {
        questionAPI.addWrong({ question: q.id, exam_type: '필기', is_mock: true }).catch(() => {});
      });

    } else {
      // 실기: 제출만 하고 결과는 채점 후 직접 확인
      const unmarked = questions.filter(mq => !silgiStatus[mq.id]);
      if (unmarked.length > 0) {
        if (!window.confirm(`${unmarked.length}문제가 풀음/못풀음 표시가 없습니다. 제출하시겠습니까?`)) return;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      setSubmitted(true);
      // 못풀음 표시된 문제는 자동으로 오답 처리
      setSilgiStatus(prev => {
        const next = { ...prev };
        questions.forEach(mq => {
          if (next[mq.id] === 'unsolved') next[mq.id] = 'wrong';
        });
        return next;
      });
      alert('제출 완료! 각 문제의 정답/오답을 선택한 후 결과 버튼을 클릭하세요.');
    }
  };

  // 실기 채점 버튼 클릭
  const handleSilgiGrade = (qId: number, result: 'correct' | 'wrong') => {
    const newStatus = { ...silgiStatus, [qId]: result };
    setSilgiStatus(newStatus);

    // 오답이면 오답노트 등록
    if (result === 'wrong') {
      questionAPI.addWrong({ question: qId, exam_type: examType, is_mock: true }).catch(() => {});
    }

    // ✅ 모든 문제 채점 완료 시 결과 저장
    const allGraded = questions.every(q =>
      newStatus[q.id] === 'correct' || newStatus[q.id] === 'wrong'
    );
    if (allGraded && submitted) {
      const correctCnt = Object.values(newStatus).filter(s => s === 'correct').length;
      const totalScore = questions.reduce((sum, q) => {
        if (newStatus[q.id] === 'correct') {
          return sum + (silgiScores[q.id] ?? diffToScore(q.difficulty || '하'));
        }
        return sum;
      }, 0);

      questionAPI.saveMockResult({
        subject_id: subject.id,
        exam_type: '실기',
        mock_type: mockType,
        score: totalScore,
        correct_count: correctCnt,
        total_count: questions.length,
        subject_stats: {},
      }).catch(() => {});
    }
  };

  // 이미지 파싱
  const parseImgTag = (content: string) => {
    const match = content.match(/^\[(\d+)img:(.+)\]$/i);
    if (match) return { scale: Number(match[1]), path: match[2].trim() };
    return null;
  };

  // 문제/보기 텍스트에서 [N:img:경로], <br>, _x000d_, [색상]텍스트[/] 파싱하여 렌더링
  const COLOR_MAP: Record<string, string> = {
    '빨강': '#e74c3c', '빨간': '#e74c3c', '빨간색': '#e74c3c',
    '파랑': '#3498db', '파란': '#3498db', '파란색': '#3498db',
    '초록': '#27ae60', '초록색': '#27ae60', '녹색': '#27ae60',
    '주황': '#e67e22', '주황색': '#e67e22', '오렌지': '#e67e22',
    '보라': '#9b59b6', '보라색': '#9b59b6',
    '노랑': '#f1c40f', '노란': '#f1c40f', '노란색': '#f1c40f',
    '분홍': '#e91e63', '분홍색': '#e91e63', '핑크': '#e91e63',
    '갈색': '#8b4513', '회색': '#7f8c8d', '그레이': '#7f8c8d',
    '검정': '#000000', '검정색': '#000000', '검은': '#000000',
    'red': '#e74c3c', 'blue': '#3498db', 'green': '#27ae60',
    'orange': '#e67e22', 'purple': '#9b59b6', 'yellow': '#f1c40f',
    'pink': '#e91e63', 'brown': '#8b4513', 'gray': '#7f8c8d',
    'grey': '#7f8c8d', 'black': '#000000',
  };

  // ✅ V8 수식 한 개 ($...$ → KaTeX)
  // 크기를 지정하지 않는다. KaTeX 가 em 단위로 그리므로 바깥 fontSize 를 그대로 따라간다.
  const renderMath = (tex: string, key: React.Key): React.ReactNode => {
    try {
      const html = katex.renderToString(tex, {
        throwOnError: false,   // 못 읽는 식은 붉게 표시만 되고 화면은 안 죽는다
        displayMode: false,    // 글줄 안에 흐르도록
        strict: false,
      });
      return <span key={key} dangerouslySetInnerHTML={{ __html: html }} />;
    } catch (e) {
      return <span key={key}>{'$' + tex + '$'}</span>;
    }
  };

  // ✅ V8 색상 구간처럼 이미 잘려 나온 조각 안에서 $수식$ 만 따로 처리
  const renderWithMath = (text: string, keyBase: string): React.ReactNode => {
    if (!text) return null;
    const bits = text.split(/(\$[^$\n]+\$)/g);
    return bits.map((b, j) =>
      /^\$[^$\n]+\$$/.test(b)
        ? renderMath(b.slice(1, -1), `${keyBase}-m${j}`)
        : <span key={`${keyBase}-t${j}`}>{b}</span>
    );
  };

  // $수식$ 도 함께 처리한다 (✅ V8)
  const renderText = (text: string): React.ReactNode => {
    if (!text) return null;
    let cleaned = text.replace(/_x000d_/gi, '').replace(/\r/g, '');
    // ✅ [색상]텍스트[/] → 색상 처리 (여는 태그는 COLOR_MAP에 등록된 색상명일 때만 인식)
    //    내부에 [kW],[A],[V] 등 단위 대괄호가 있어도 색칠 범위가 깨지지 않음
    const colorNames = Object.keys(COLOR_MAP)
      .sort((a, b) => b.length - a.length)
      .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const colorTagRe = new RegExp('\\[(' + colorNames.join('|') + ')\\]([\\s\\S]*?)\\[\\/\\]', 'gi');
    cleaned = cleaned.replace(colorTagRe, (match, color, content) => {
      const code = COLOR_MAP[color.toLowerCase()] || COLOR_MAP[color];
      if (code) return `@@COLOR:${code}@@${content}@@/COLOR@@`;
      return match;
    });
    // ✅ V8 맨 뒤에 $수식$ 추가. 앞의 네 가지는 손대지 않았다.
    const brPattern = new RegExp('(\\[\\d+:img:[^\\]]+\\]|\\[\\d+img:[^\\]]+\\]|<br\\s*\\/?>|@@COLOR:[^@]+@@[^@]*@@\\/COLOR@@|\\$[^$\\n]+\\$)', 'gi');
    const parts = cleaned.split(brPattern);
    return (
      <>
        {parts.map((part, i) => {
          const imgMatch = part.match(/^\[(\d+):img:(.+)\]$/) || part.match(/^\[(\d+)img:(.+)\]$/);
          if (imgMatch) {
            const scale = Number(imgMatch[1]);
            const path = imgMatch[2].trim();
            const src = path.startsWith('http') ? path : `/images/${path}`;
            return (
              <img key={i} src={src} alt="문제 이미지"
                style={{ width: `${scale}%`, maxWidth: '100%', borderRadius: 4, verticalAlign: 'middle' }}
                onError={e => (e.currentTarget.style.display = 'none')} />
            );
          }
          if (/^<br\s*\/?>$/i.test(part)) return <br key={i} />;
          // ✅ V8 수식
          if (/^\$[^$\n]+\$$/.test(part)) return renderMath(part.slice(1, -1), i);
          // 색상 태그 (✅ V8 색칠한 구간 안에 수식이 섞여 있어도 그려지도록)
          const colorMatch = part.match(/^@@COLOR:([^@]+)@@(.*)@@\/COLOR@@$/);
          if (colorMatch) return <span key={i} style={{ color: colorMatch[1] }}>{renderWithMath(colorMatch[2], `c${i}`)}</span>;
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  };

  const renderRestContent = (content: string) => {
    if (!content) return null;
    const imgTag = parseImgTag(content);
    if (imgTag) {
      const src = imgTag.path.startsWith('http') ? imgTag.path : `/images/${imgTag.path}`;
      return <div style={{ textAlign: 'center' }}>
        <img src={src} alt="휴식" style={{ width: `${imgTag.scale}%`, borderRadius: 6 }}
          onError={e => (e.currentTarget.style.display = 'none')} />
      </div>;
    }
    if (content.includes('youtu')) {
      const match = content.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      if (match?.[1]) return <iframe width="100%" height="100"
        src={`https://www.youtube.com/embed/${match[1]}`} frameBorder="0" allowFullScreen title="휴식" />;
    }
    return <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{content}</div>;
  };

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  const isWarning = remaining <= 300; // 5분 이하 경고

  return (
    <div className="mock-layout">
      {/* 동영상 팝업 */}
      {videoUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', background: '#000', borderRadius: 12, padding: 10, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => videoCanClose && setVideoUrl(null)}
              style={{ position: 'absolute', top: -16, right: -16, width: 36, height: 36,
                background: videoCanClose ? '#e74c3c' : '#888',
                color: 'white', border: 'none', borderRadius: '50%', fontSize: videoCanClose ? 20 : 13,
                cursor: videoCanClose ? 'pointer' : 'not-allowed', zIndex: 1, fontWeight: 700 }}>
              {videoCanClose ? '✕' : videoCountdown}
            </button>
            {getYouTubeId(videoUrl) ? (
              <iframe width="800" height="450"
                src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}?autoplay=1`}
                frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen style={{ borderRadius: 6, maxWidth: '85vw', maxHeight: '80vh', display: 'block' }} title="동영상" />
            ) : videoUrl.match(/\.(mp4|webm|ogg)$/i) ? (
              <video controls autoPlay style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 6 }}>
                <source src={videoUrl} />
              </video>
            ) : (
              <div style={{ padding: 20, color: 'white', textAlign: 'center' }}>
                <a href={videoUrl} target="_blank" rel="noreferrer"
                  style={{ color: '#3498db', textDecoration: 'underline' }}>{videoUrl}</a>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 결과 오버레이 */}
      {showResult && submitted && (
        <div className="mock-result-overlay">
          <div className="mock-result-card">
            <div className="mock-result-title">📊 {mockType === '일반' ? '일반' : mockType}-모의고사 결과</div>
            <div className="mock-result-subject">{subject.name} [{examType}]</div>
            {examType === '필기' ? (
              <>
                <div className={`mock-score ${score >= 60 ? 'pass' : 'fail'}`}>{score}점</div>
                <div className="mock-score-label">{score >= 60 ? '🎉 합격!' : '📚 불합격'}</div>
                <div className="mock-result-stats">
                  <div className="result-stat"><span>총 문항</span><strong>{questions.length}</strong></div>
                  <div className="result-stat correct"><span>정답</span><strong>{correctCount}</strong></div>
                  <div className="result-stat wrong"><span>오답</span><strong>{questions.length - correctCount}</strong></div>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                  각 문제의 정답/오답을 선택하면 점수가 계산됩니다.
                </div>
                <div className="mock-result-stats">
                  <div className="result-stat"><span>총 문항</span><strong>{questions.length}</strong></div>
                  <div className="result-stat correct"><span>정답</span><strong>{Object.values(silgiStatus).filter(s => s === 'correct').length}</strong></div>
                  <div className="result-stat wrong"><span>오답</span><strong>{Object.values(silgiStatus).filter(s => s === 'wrong').length}</strong></div>
                  <div className="result-stat"><span>미채점</span><strong>{questions.length - Object.values(silgiStatus).filter(s => s === 'correct' || s === 'wrong').length}</strong></div>
                </div>
                <div className={`mock-score ${silgiTotalScore >= 60 ? 'pass' : 'fail'}`}>{silgiTotalScore}점</div>
                <div className="mock-score-label">{silgiTotalScore >= 60 ? '🎉 합격!' : '📚 계속 도전!'}</div>
              </>
            )}
            <div className="mock-result-btns">
              <button className="btn btn-ghost" onClick={onBack}>← 종목선택</button>
              <button className="btn btn-warning" onClick={startNormal}>🔄 다시 풀기</button>
              <button className="btn btn-primary" onClick={() => {
                // ✅ 문제검토: 가장 최근 년도/차수로 자동 선택 후 검토모드 진입
                const validYears = Array.from(new Set(questions.map(q => String(q.year)).filter(Boolean))).sort();
                const latestYear = validYears[validYears.length - 1] || '';
                const validRounds = Array.from(new Set(
                  questions.filter(q => String(q.year) === latestYear).map(q => String(q.round)).filter(Boolean)
                )).sort((a, b) => Number(a) - Number(b));
                const latestRound = validRounds[validRounds.length - 1] || '';
                setReviewYear(latestYear);
                setReviewRound(latestRound);
                setReviewMode(true);
                setShowResult(false);
                // 해당 년도/차수 첫 문제로 이동
                const firstIdx = questions.findIndex(q =>
                  String(q.year) === latestYear && String(q.round) === latestRound
                );
                if (firstIdx >= 0) setCurIdx(firstIdx);
              }}>문제 검토</button>
            </div>
          </div>
        </div>
      )}

      {/* 상단 topbar */}
      <div className="q-topbar">
        <div className="q-topbar-left">
          <button className="btn btn-sm q-topbar-back-btn" onClick={onBack}>← 이전</button>
          <div className="q-topbar-info">
            <span className="q-topbar-title">{subject.name} [{examType}] {mockType === '일반' ? '일반' : mockType}-모의고사</span>
            <span className="q-topbar-count">답안 {totalAnswered}/{questions.length}</span>
          </div>
          <div className="q-topbar-modes">
            <button className="q-mode-btn" onClick={() => onModeChange?.('normal')}>일반</button>
            <button className="q-mode-btn active">모의고사</button>
            <button className="q-mode-btn" onClick={() => onModeChange?.('wrong')}>오답노트</button>
          </div>
        </div>

        {/* 중앙 - 큰 타이머 */}
        <div className="mock-timer-center" style={{ color: isWarning ? '#ef4444' : 'white' }}>
          <span style={{ fontSize: 10, opacity: 0.7 }}>⏱ 남은 시간</span>
          <span className="mock-timer-display" style={{ color: isWarning ? '#ef4444' : 'white' }}>
            {formatTime(remaining)}
          </span>
          {isWarning && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 700 }}>⚠️ 시간 부족!</span>}
        </div>

        <div className="q-topbar-right">
          {/* 폰트 크기 슬라이더 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '3px 8px' }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap' }}>🔤 {fontSize}px</span>
            <input type="range" min={12} max={26} value={fontSize}
              onChange={e => setFontSize(Number(e.target.value))}
              style={{ width: 80, accentColor: 'var(--primary)', cursor: 'pointer' }} />
          </div>
          <select className="q-mock-select" value={filterYear}
            onChange={e => setFilterYear(e.target.value)}>
            <option value="">년도 전체</option>
            {years.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
          <input type="number" className="q-mock-select" style={{ width: 60 }}
            value={mockCount} min={1} max={200}
            onChange={e => setMockCount(+e.target.value)} />
          <button className="q-mode-btn" onClick={startNormal}>🔄 재시작</button>
          <span className="q-topbar-user">사용자: <strong>{user?.username}</strong></span>
        </div>
      </div>

      {/* ✅ 문제검토 필터바 */}
      {reviewMode && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '6px 16px', background: '#eff6ff',
          borderBottom: '2px solid var(--primary)', flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>📖 문제검토</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>년도/차수 선택 →</span>
          <select className="form-select" style={{ width: 100, fontSize: 12, height: 28, padding: '2px 6px' }}
            value={reviewYear}
            onChange={e => {
              setReviewYear(e.target.value);
              setReviewRound('');
              // 해당 년도 첫 문제로 이동
              const firstIdx = questions.findIndex(q => String(q.year) === e.target.value);
              if (firstIdx >= 0) setCurIdx(firstIdx);
            }}>
            <option value="">년도 전체</option>
            {reviewYears.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select className="form-select" style={{ width: 80, fontSize: 12, height: 28, padding: '2px 6px' }}
            value={reviewRound}
            onChange={e => {
              setReviewRound(e.target.value);
              // 해당 차수 첫 문제로 이동
              const firstIdx = questions.findIndex(q =>
                (!reviewYear || String(q.year) === reviewYear) && String(q.round) === e.target.value
              );
              if (firstIdx >= 0) setCurIdx(firstIdx);
            }}>
            <option value="">차수 전체</option>
            {reviewRounds.map(r => <option key={r} value={r}>{r}차</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {reviewQuestions.length}문제
          </span>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginLeft: 'auto' }}
            onClick={() => setReviewMode(false)}>
            ✕ 검토 종료
          </button>
        </div>
      )}

      {/* 본문 */}
      <div className="mock-body">

        {/* 왼쪽: 문제목록 + D/R/S 버튼 */}
        <div className="mock-sidebar" style={{ width: sidebarR.width }}>
          <div className="mock-sidebar-title">
            {reviewMode
              ? `검토: ${reviewYear || '전체'}년 ${reviewRound ? reviewRound+'차' : '전체'} (${reviewQuestions.length}문제)`
              : (mockType === '일반' ? '일반모의고사' : `${mockType}-모의고사`) + ' 문항'
            }
          </div>
          <div className="mock-q-list" ref={mockListRef}>
            {(reviewMode ? reviewQuestions : questions).map((mq, i) => {
              // reviewMode일 때 실제 questions 내 인덱스 찾기
              const realIdx = reviewMode ? questions.findIndex(q => q.id === mq.id) : i;
              const ans = userAns[mq.id] !== undefined;
              const isActive = realIdx === curIdx;
              const subjectColor = getSubjectColor(mq.subject_name || '');
              const correct = submitted && ans && String(userAns[mq.id]) === String(mq.answer);
              const wrong = submitted && ans && String(userAns[mq.id]) !== String(mq.answer);
              const prevMq = i > 0 ? (reviewMode ? reviewQuestions : questions)[i-1] : null;
              const curSubject = mq.subject_name || '';
              const isNewSubject = i > 0 && (prevMq?.subject_name || '') !== curSubject;
              return (
                <React.Fragment key={mq.id}>
                  {(i === 0 || isNewSubject) && curSubject && (
                    <div className="q-subject-divider"
                      style={{ '--subject-color': subjectColor } as React.CSSProperties}>
                      {curSubject}
                    </div>
                  )}
                  <div
                    className={`mock-q-item ${isActive ? 'active' : ''} ${ans ? 'answered' : ''} ${correct ? 'correct' : ''} ${wrong ? 'wrong' : ''}`}
                    style={{ '--subject-color': subjectColor } as React.CSSProperties}
                    onClick={() => setCurIdx(realIdx)}>
                    <span>
                      [{mq.year}-{mq.round}]&nbsp;
                      <span style={{ color: subjectColor, fontWeight: 700 }}>{mq.subject_name || ''}</span>
                      &nbsp;{reviewMode ? (realIdx + 1) : (i + 1)}번
                      {mockType === 'S' && (
                        <span style={{ fontSize: 9, marginLeft: 3, padding: '1px 4px', borderRadius: 4, fontWeight: 700,
                          background: mq._isWrong ? '#fee2e2' : '#fef3c7',
                          color: mq._isWrong ? '#e74c3c' : '#d97706' }}>
                          {mq._isWrong ? '오답' : '보충'}
                        </span>
                      )}
                    </span>
                    <span>{ans ? '✓' : ''}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* D/R/S 버튼 */}
          <div className="mock-type-btns">
            <button className="mock-type-btn d" onClick={startD}>📋 D모의고사</button>
            <button className="mock-type-btn r" onClick={startR}>🔀 R모의고사</button>
            <button className="mock-type-btn s" onClick={startS}>⭐ S모의고사</button>
          </div>
        </div>

        <div className="q-resizer" onMouseDown={sidebarR.onMouseDown} />

        {/* 문제 영역 */}
        <div className="mock-question-area">
          {q && (
            <>
              <div className="q-header" style={{ marginBottom: 16 }}>
                <div className="q-info">
                  <span className={`q-type-badge ${examType === '실기' ? 'silgi' : ''}`}>{examType}</span>
                  <span className="q-subject-badge" style={{ background: getSubjectColor(q.subject_name || '') + '22', color: getSubjectColor(q.subject_name || '') }}>
                    {q.subject_name || subject.name}
                  </span>
                  <span className="q-meta">{q.year}년 {q.round}차 | {q.q_type || '-'}</span>
                  {q.video && (q.video.includes('http') || q.video.includes('youtu')) && (
                    <span style={{ cursor: 'pointer', fontSize: 18 }} title="동영상 보기"
                      onClick={() => openVideo(q.video)}>📹</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {/* 실기: 점수 입력 */}
                  {examType === '실기' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>점수</span>
                      <input type="number" min={1} max={20}
                        value={silgiScores[q.id] ?? diffToScore(q.difficulty || '하')}
                        onChange={e => setSilgiScores(prev => ({ ...prev, [q.id]: Number(e.target.value) }))}
                        style={{ width: 52, textAlign: 'center', fontSize: 14, fontWeight: 700, border: '2px solid var(--primary)', borderRadius: 6, padding: '2px 4px' }} />
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>점</span>
                      {q.difficulty && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700,
                          background: q.difficulty === '상' ? '#fee2e2' : q.difficulty === '중' ? '#fef3c7' : '#dcfce7',
                          color: q.difficulty === '상' ? '#e74c3c' : q.difficulty === '중' ? '#d97706' : '#16a34a' }}>
                          {q.difficulty}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="q-progress">{curIdx + 1} / {questions.length}</div>
                </div>
              </div>

              <div className="q-text" style={{ fontSize, marginBottom: 20, lineHeight: 2 }}>
                <strong style={{ color: 'var(--primary)', marginRight: 8 }}>{curIdx + 1}.</strong>
                {renderText(q.question)}
              </div>

              {q.image && (
                <div className="q-image">
                  <img src={q.image} alt="문제" onError={e => (e.currentTarget.style.display = 'none')} />
                </div>
              )}

              {examType === '필기' && (
                <div className="q-options">
                  {[1,2,3,4].map(n => {
                    const optText = q[`option${n}`];
                    if (!optText) return null;
                    const selected = userAns[q.id] === n;
                    const correct = Number(n) === Number(q.answer);
                    return (
                      <button key={n}
                        className={`q-option ${selected ? 'selected' : ''} ${submitted && correct ? 'correct' : ''} ${submitted && selected && !correct ? 'wrong' : ''}`}
                        onClick={() => { if (submitted) return; setUserAns(prev => ({ ...prev, [q.id]: n })); }}>
                        <span className="option-num">{['①','②','③','④'][n-1]}</span>
                        <span className="option-text" style={{ fontSize }}>{renderText(optText)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {examType === '실기' && (
                <>
                  <textarea className="form-control" rows={4} placeholder="답안을 입력하세요..."
                    value={userAns[q.id] || ''}
                    onChange={e => setUserAns(prev => ({...prev, [q.id]: e.target.value}))}
                    disabled={submitted} style={{ resize: 'vertical', marginBottom: 10 }} />
                  {/* 풀음/못풀음 버튼 (제출 전후 모두) */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {(['solved','unsolved'] as const).map(s => {
                      const isActive = silgiStatus[q.id] === s;
                      return (
                        <button key={s}
                          onClick={() => setSilgiStatus(prev => ({ ...prev, [q.id]: s }))}
                          style={{ padding: '6px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                            border: `2px solid ${s === 'solved' ? '#3498db' : '#95a5a6'}`,
                            background: isActive ? (s === 'solved' ? '#3498db' : '#95a5a6') : 'white',
                            color: isActive ? 'white' : (s === 'solved' ? '#3498db' : '#95a5a6') }}>
                          {s === 'solved' ? '✏️ 풀음' : '❓ 못풀음'}
                        </button>
                      );
                    })}
                    {/* 정답/오답 버튼 (제출 후 + 풀음 상태일 때만 활성) */}
                    {(['correct','wrong'] as const).map(s => {
                      const isActive = silgiStatus[q.id] === s;
                      const enabled = submitted && silgiStatus[q.id] !== 'unsolved';
                      return (
                        <button key={s}
                          disabled={!enabled}
                          onClick={() => enabled && handleSilgiGrade(q.id, s)}
                          title={!submitted ? '제출 후 채점 가능' : silgiStatus[q.id] === 'unsolved' ? '못풀음 문제는 채점 불가' : ''}
                          style={{ padding: '6px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                            cursor: enabled ? 'pointer' : 'not-allowed',
                            border: `2px solid ${!enabled ? '#ddd' : s === 'correct' ? '#27ae60' : '#e74c3c'}`,
                            background: !enabled ? '#f5f5f5' : isActive ? (s === 'correct' ? '#27ae60' : '#e74c3c') : 'white',
                            color: !enabled ? '#bbb' : isActive ? 'white' : (s === 'correct' ? '#27ae60' : '#e74c3c') }}>
                          {s === 'correct' ? '⭕ 정답' : '❌ 오답'}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && silgiStatus[q.id] === 'correct' && (
                    <div style={{ fontSize: 12, color: 'var(--success)', fontWeight: 700 }}>
                      +{silgiScores[q.id] ?? diffToScore(q.difficulty || '하')}점 획득!
                    </div>
                  )}
                </>
              )}

              {submitted && q && showExp[q.id] && (
                <div className="q-answer-section">
                  <div className="q-answer">
                    <span className="answer-label">✓ 정답</span>
                    <span className="answer-value">{examType === '필기' ? `${q.answer}번` : renderText(q.answer)}</span>
                    {examType === '필기' && (
                      <span className={`answer-result ${String(userAns[q.id]) === String(q.answer) ? 'correct' : 'wrong'}`}>
                        {String(userAns[q.id]) === String(q.answer) ? '🎉 정답!' : '❌ 오답'}
                      </span>
                    )}
                  </div>
                  {q.explanation && (
                    <div className="q-explanation">
                      <div className="exp-label">📝 해설</div>
                      <div className="exp-text" style={{ fontSize }}>{renderText(q.explanation)}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="q-buttons" style={{ marginTop: 16 }}>
                <button className="btn btn-ghost" onClick={() => setCurIdx(i => Math.max(0, i-1))} disabled={curIdx === 0}>이전</button>
                <button
                  className="btn btn-ghost"
                  disabled={!submitted}
                  onClick={() => q && setShowExp(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                >정답확인</button>
                <button className="btn btn-primary" onClick={() => setCurIdx(i => Math.min(questions.length-1, i+1))} disabled={curIdx === questions.length-1}>다음</button>
                <button
                  className="btn btn-ghost"
                  disabled={!submitted || !q || !showExp[q?.id]}
                  onClick={() => q && setShowExp(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                >해설보기</button>
                <button
                  className="btn btn-ghost"
                  style={{ background: '#ede9fe', color: submitted ? '#6d28d9' : undefined }}
                  disabled={!submitted}
                  onClick={() => setShowComment(!showComment)}
                >💬 함께공부해요</button>
                {!submitted
                  ? <button className="btn btn-danger" onClick={handleSubmit}>📊 제출</button>
                  : <button className="btn btn-success" onClick={() => {
                      if (examType === '실기') {
                        // 풀음 상태인데 정답/오답 미선택인 문제 확인
                        const ungraded = questions.filter(mq =>
                          silgiStatus[mq.id] === 'solved'
                        );
                        if (ungraded.length > 0) {
                          alert(`아직 ${ungraded.length}문제가 채점되지 않았습니다.\n풀음으로 표시된 문제의 정답/오답을 선택해주세요.`);
                          return;
                        }
                      }
                      setShowResult(true);
                    }}>📊 결과</button>
                }
                <button className="btn btn-ghost" onClick={onBack}>종료</button>
              </div>
            </>
          )}
        </div>

        <div className="q-resizer" onMouseDown={answerSheetR.onMouseDown} />

        {/* 답안 표기란 */}
        <div className="answer-sheet-panel" style={{ width: answerSheetR.width }}>
          <div className="answer-sheet-header" onClick={() => setShowAnswerSheet(!showAnswerSheet)}>
            <span>📋 답안 표기란</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
              {!submitted && (
                <button
                  onClick={() => setShowRemaining(!showRemaining)}
                  style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 12,
                    border: `1px solid ${showRemaining ? 'var(--danger)' : 'var(--border)'}`,
                    background: showRemaining ? '#fee2e2' : 'white',
                    color: showRemaining ? 'var(--danger)' : 'var(--text-muted)',
                    cursor: 'pointer', fontWeight: 700,
                  }}>
                  ❗ 남은 {questions.filter(q => userAns[q.id] === undefined).length}문제
                </button>
              )}
              <span>{showAnswerSheet ? '▲' : '▼'}</span>
            </div>
          </div>

          {/* 남은 문제 목록 */}
          {showRemaining && !submitted && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', background: '#fff7f7' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginBottom: 6 }}>
                미답변 문제
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {questions.map((mq, i) => {
                  if (userAns[mq.id] !== undefined) return null;
                  return (
                    <button key={mq.id}
                      onClick={() => { setCurIdx(i); setShowRemaining(false); }}
                      style={{
                        width: 30, height: 30, borderRadius: '50%',
                        border: '2px solid var(--danger)',
                        background: i === curIdx ? 'var(--danger)' : 'white',
                        color: i === curIdx ? 'white' : 'var(--danger)',
                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      }}>
                      {i + 1}
                    </button>
                  );
                })}
                {questions.filter(q => userAns[q.id] === undefined).length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--success)' }}>✅ 모든 문제 답변 완료!</span>
                )}
              </div>
            </div>
          )}
          {showAnswerSheet && (
            <div className="answer-sheet">
              {/* 실기: 채점 현황 표시 */}
              {examType === '실기' && submitted && (
                <div style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid var(--border)', fontSize: 11 }}>
                  {(() => {
                    const gradedCount = Object.values(silgiStatus).filter(s => s === 'correct' || s === 'wrong').length;
                    const allGraded = gradedCount === questions.length;
                    return allGraded ? (
                      <div style={{ textAlign: 'center', color: 'var(--success)', fontWeight: 700 }}>
                        총점: {silgiTotalScore}점
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', color: 'var(--warning)' }}>
                        채점 {gradedCount}/{questions.length} 완료
                      </div>
                    );
                  })()}
                </div>
              )}
              {questions.map((mq, i) => {
                const ans = userAns[mq.id];
                const isActive = i === curIdx;
                // 필기
                if (examType === '필기') {
                  const isCorrect = submitted && ans !== undefined && String(ans) === String(mq.answer);
                  const isWrong = submitted && ans !== undefined && String(ans) !== String(mq.answer);
                  return (
                    <div key={mq.id}
                      className={`answer-row ${isActive ? 'active' : ''} ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''}`}
                      onClick={() => setCurIdx(i)}>
                      <span className="answer-row-num">{i + 1}</span>
                      <div className="answer-row-circles">
                        {[1,2,3,4].map(n => (
                          <button key={n}
                            className={`answer-circle ${ans === n ? 'selected' : ''} ${submitted && Number(n) === Number(mq.answer) ? 'correct-circle' : ''} ${submitted && ans === n && Number(n) !== Number(mq.answer) ? 'wrong-circle' : ''}`}
                            onClick={e => {
                              e.stopPropagation();
                              if (submitted) return;
                              setUserAns(prev => ({ ...prev, [mq.id]: n }));
                              setCurIdx(i);
                            }}>
                            {['①','②','③','④'][n-1]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                // 실기
                const status = silgiStatus[mq.id];
                const score = silgiScores[mq.id] ?? diffToScore(mq.difficulty || '하');
                return (
                  <div key={mq.id}
                    className={`answer-row ${isActive ? 'active' : ''} ${status === 'correct' ? 'correct' : ''} ${status === 'wrong' ? 'wrong' : ''}`}
                    onClick={() => setCurIdx(i)}
                    style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4, padding: '6px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                      <span className="answer-row-num">{i + 1}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{score}점</span>
                      {status === 'correct' && <span style={{ fontSize: 10, color: 'var(--success)', fontWeight: 700, marginLeft: 'auto' }}>⭕</span>}
                      {status === 'wrong' && <span style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 700, marginLeft: 'auto' }}>❌</span>}
                      {status === 'solved' && <span style={{ fontSize: 10, color: '#3498db', fontWeight: 700, marginLeft: 'auto' }}>✏️</span>}
                      {status === 'unsolved' && <span style={{ fontSize: 10, color: '#95a5a6', fontWeight: 700, marginLeft: 'auto' }}>❓</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {(['solved','unsolved'] as const).map(s => (
                        <button key={s}
                          onClick={e => { e.stopPropagation(); setSilgiStatus(prev => ({ ...prev, [mq.id]: s })); setCurIdx(i); }}
                          style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, cursor: 'pointer', fontWeight: 700,
                            border: `1px solid ${s === 'solved' ? '#3498db' : '#95a5a6'}`,
                            background: status === s ? (s === 'solved' ? '#3498db' : '#95a5a6') : 'white',
                            color: status === s ? 'white' : (s === 'solved' ? '#3498db' : '#95a5a6') }}>
                          {s === 'solved' ? '풀음' : '못풀음'}
                        </button>
                      ))}
                      {(['correct','wrong'] as const).map(s => {
                        const gradeEnabled = submitted && status !== 'unsolved';
                        return (
                          <button key={s}
                            disabled={!gradeEnabled}
                            onClick={e => { e.stopPropagation(); if (gradeEnabled) { handleSilgiGrade(mq.id, s); setCurIdx(i); } }}
                            style={{ fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 700,
                              cursor: gradeEnabled ? 'pointer' : 'not-allowed',
                              border: `1px solid ${!gradeEnabled ? '#ddd' : s === 'correct' ? '#27ae60' : '#e74c3c'}`,
                              background: !gradeEnabled ? '#f5f5f5' : status === s ? (s === 'correct' ? '#27ae60' : '#e74c3c') : 'white',
                              color: !gradeEnabled ? '#bbb' : status === s ? 'white' : (s === 'correct' ? '#27ae60' : '#e74c3c') }}>
                            {s === 'correct' ? '정답' : '오답'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="q-resizer" onMouseDown={restPanelR.onMouseDown} />

        {/* 휴식박스 */}
        <div className="mock-rest-panel" style={{ width: restPanelR.width }}>
          <div className="rest-header">
            <span>🎨 휴식</span>
          </div>
          <div className="rest-scroll" ref={restScrollRef}>
            {displayBoxes.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>휴식 콘텐츠 없음</div>
              : displayBoxes.map((box, i) => (
                <div key={i} className="rest-item">{renderRestContent(box)}</div>
              ))
            }
          </div>
        </div>

        {/* ✅ RightPanel 리사이저 + 해설PDF 패널 (문제검토 모드일 때만) */}
        {reviewMode && (
          <>
            <div className="q-resizer-rest" onMouseDown={rightPanelR.onMouseDown} />
            <RightPanel
              width={rightPanelR.width}
              explanationPage={q?.explanation_page ?? null}
              showExplanation={true}
              year={reviewYear ? Number(reviewYear) : (q?.year ? Number(q.year) : null)}
              round={reviewRound ? Number(reviewRound) : (q?.round ? Number(q.round) : null)}
              subjectId={subject?.id ?? null}
              subjectName={examType === '필기' ? (q?.subject_name ?? null) : null}
              examType={examType}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MockExam;
